/**
 * om_yoga_ui — API client
 * All calls proxied to API Gateway via Vite → http://127.0.0.1:8000
 * Client: radhekrishna (OM Yoga multi-studio platform)
 */

const API_BASE = '/api/yoga'
const _YOGA_CLIENT_ID = 'club-of-royals-0f3104'
const PRACTITIONER_META_KEY = 'yoga_practitioner_meta'
const TENANT_BOOKINGS_KEY = 'yoga_tenant_bookings'
const TENANT_CLASS_SAMPLES_KEY = 'yoga_tenant_class_samples'
const TENANT_CLIENT_ID_MAP: Record<string, string> = {
  allscrum: 'all-scrum-d2d807',
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface YogaUser {
  user_id: number
  email: string
  username: string
  full_name?: string | null
  client_id: string
  role: string
  permissions?: string[]
  selected_package: string
  trial_ends_at?: string | null
  // Yoga-profile extras (present after /auth/register; optional after central /auth/login)
  user_type?: string | null
  specialization?: string | null
  studio_name?: string | null
  location?: string | null
  ai_agents?: string[]
  package_name?: string
  // Practitioner identity — returned from auth login when yoga_user_profiles row exists
  practitioner_type?: string | null  // yoga_teacher | ayurveda_practitioner | astrologer | etc.
  employment_type?: string | null    // independent | employed | both
  // Present only on a delegated "act as studio admin" session (see
  // src/lib/delegatedSession.ts) — identifies who is delegating this
  // session, so the UI can show an "Acting as..." banner + exit path.
  delegated_by?: { user_id: number; name: string; email: string } | null
}

export interface LoginResponse {
  access_token: string
  user?: YogaUser
}

export interface RegisterRequest {
  email: string
  password: string
  full_name: string
  username: string
  phone?: string
  user_type: 'instructor' | 'studio_admin' | 'franchise_owner'
  specialization?: string
  yoga_styles?: string[]
  studio_name?: string
  studio_reg_no?: string
  location?: string
  selected_package: string
}

export interface RegisterResponse {
  access_token: string
  user: YogaUser
  message: string
}

export interface YogaPackage {
  id: string
  name: string
  tagline: string
  badge: string | null
  price_monthly: number
  price_annual: number
  annual_saving: number
  cta: string
  highlighted: boolean
  user_types: string[]
  limits: Record<string, number | boolean>
  ai_agents: string[]
  features: string[]
  not_included: string[]
}

export interface AgentCapability {
  name: string
  category: string
  description: string
}

export interface AgentTool {
  name: string
  description: string
  category: string
  version: string
}

export interface AgentSkill {
  skill_id: string
  name: string
  description: string
  input_schema: Record<string, unknown>
  output_schema: Record<string, unknown>
  required_scopes: string[]
  streaming: boolean
  tags: string[]
}

export interface YogaAgent {
  agent_key: string
  agent_id: string
  agent_name: string
  agent_role: string
  agent_goal: string
  framework: string
  category: string
  agent_type: string
  version: string
  status: string
  tags: string[]
  min_package: string
  capabilities: AgentCapability[]
  tools_count?: number
  skills_count?: number
  tools?: AgentTool[]
  skills?: AgentSkill[]
  unlocked: boolean
  locked_reason?: string | null
  upgrade_to?: string | null
}

export interface AgentListResponse {
  agents: YogaAgent[]
  total: number
  unlocked: number
  locked: number
  package: string
  client_id: string
}

export interface MarketplaceResponse extends AgentListResponse {
  categories: Record<string, number>
  packages: YogaPackage[]
}

// ─── Auth helpers ─────────────────────────────────────────────────────────────

function getToken(): string | null {
  return localStorage.getItem('yoga_token')
}

export function saveSession(token: string, user: YogaUser): void {
  localStorage.setItem('yoga_token', token)
  localStorage.setItem('yoga_user', JSON.stringify(user))
}

export function clearSession(): void {
  localStorage.removeItem('yoga_token')
  localStorage.removeItem('yoga_user')
}

export function getSavedUser(): YogaUser | null {
  const raw = localStorage.getItem('yoga_user')
  if (!raw) return null
  try {
    const user = JSON.parse(raw) as YogaUser
    return user
  } catch {
    return null
  }
}

export function authHeaders(): HeadersInit {
  const token = getToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

const DEFAULT_TIMEOUT_MS = 30_000

function fetchWithTimeout(url: string, options?: RequestInit, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeoutMs)
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id))
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const body = await res.json()
      detail = body.detail ?? body.message ?? detail
    } catch {
      // non-JSON error body
    }
    throw new Error(detail)
  }
  return res.json() as Promise<T>
}

// ─── Auth endpoints ───────────────────────────────────────────────────────────

/** Thrown when the password was right but the account needs its second factor. */
export class MfaRequiredError extends Error {
  constructor() {
    super('Enter the 6-digit code from your authenticator app.')
    this.name = 'MfaRequiredError'
  }
}

export async function login(email: string, password: string, mfaCode?: string): Promise<LoginResponse> {
  // Use the central multi-tenant auth endpoint, scoped to 'radhekrishna'. The server asks for
  // `mfa_code` only for accounts that turned on two-factor (routers/mfa.py in om_open_ai).
  const res = await fetchWithTimeout('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email, password, client_id: _YOGA_CLIENT_ID,
      ...(mfaCode?.trim() ? { mfa_code: mfaCode.trim() } : {}),
    }),
  })
  if (res.status === 401) {
    const body = await res.clone().json().catch(() => ({}))
    if (body?.detail === 'mfa_required') throw new MfaRequiredError()
  }
  return handleResponse<LoginResponse>(res)
}

export async function registerYogaUser(body: RegisterRequest): Promise<RegisterResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleResponse<RegisterResponse>(res)
}

/**
 * Change the authenticated user's password.
 * Calls PATCH /api/auth/me/password — requires a valid Bearer token.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  const res = await fetchWithTimeout('/api/auth/me/password', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
  return handleResponse<{ message: string }>(res)
}

export async function logout(): Promise<void> {
  clearSession()
}

export interface FreeRegisterResult {
  access_token: string
  user_id: number
  email: string
  username: string
  full_name: string | null
  role: string
  client_id: string
  temp_password: string
}

// ─── Contact form ─────────────────────────────────────────────────────────────

export async function submitContactLead(params: {
  name: string
  email: string
  phone?: string
  service?: string
  message: string
  clientId?: string
}): Promise<{ lead_id: string; message: string }> {
  const res = await fetchWithTimeout('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: params.clientId ?? _YOGA_CLIENT_ID,
      name: params.name,
      email: params.email,
      phone: params.phone ?? '',
      service: params.service ?? '',
      message: params.message,
    }),
  })
  return handleResponse<{ lead_id: string; message: string }>(res)
}

/**
 * Register a free "viewer" member under the given client.
 * Called when an unauthenticated user clicks "Join for Free" on the Programs page.
 * Returns the issued JWT + the generated temp password so the UI can display it.
 * Free accounts are created as role=viewer — NOT instructor/studio_admin/franchise_owner.
 */
export async function registerFreeUser(
  email: string,
  fullName: string,
  clientId: string,
  phone?: string,
): Promise<FreeRegisterResult> {
  // Generate a safe temp password and username
  const rand = Math.random().toString(36).slice(2, 8)
  const tempPw = `Yoga${rand}!2`
  const base = email.split('@')[0].replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 14) || 'yogi'
  const username = `${base}${Math.floor(Math.random() * 900 + 100)}`

  const res = await fetchWithTimeout('/api/auth/register-member', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      username,
      password: tempPw,
      full_name: fullName || base,
      phone: phone || null,
      client_id: clientId,
    }),
  })
  const data = await handleResponse<{
    access_token: string
    user_id: number
    email: string
    username: string
    full_name: string | null
    role: string
    client_id: string
  }>(res)
  return { ...data, temp_password: tempPw }
}

// ─── Package endpoints ────────────────────────────────────────────────────────

export async function fetchPackages(): Promise<{ packages: YogaPackage[] }> {
  const res = await fetchWithTimeout(`${API_BASE}/packages`)
  return handleResponse<{ packages: YogaPackage[] }>(res)
}

// ─── Agent endpoints ──────────────────────────────────────────────────────────

export async function fetchAgents(category?: string): Promise<AgentListResponse> {
  const params = category ? `?category=${encodeURIComponent(category)}` : ''
  const res = await fetchWithTimeout(`${API_BASE}/agents${params}`, { headers: authHeaders() })
  return handleResponse<AgentListResponse>(res)
}

export async function fetchAgent(agentKey: string): Promise<{ agent: YogaAgent; user: { package: string; role: string }; client_id: string }> {
  const res = await fetchWithTimeout(`${API_BASE}/agents/${agentKey}`, { headers: authHeaders() })
  return handleResponse(res)
}

export async function fetchMarketplace(): Promise<MarketplaceResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/marketplace`, { headers: authHeaders() })
  return handleResponse<MarketplaceResponse>(res)
}

export async function runAgent(
  agentKey: string,
  task: string,
  context?: Record<string, unknown>,
): Promise<Response> {
  const token = getToken()
  return fetch(`${API_BASE}/agents/${agentKey}/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ task, context: context ?? {}, stream: true }),
  })
}

export interface GeneratedRequest {
  agent_key: string
  agent_name: string
  use_rml: boolean
  rml_docs: Array<{ doc_id: string; filename: string; sections: number; chunks: number }>
  generated: { task: string; context: Record<string, unknown>; stream: boolean }
  skill_schema: Record<string, unknown> | null
}

export async function generateAgentRequest(
  agentKey: string,
  useRml = false,
): Promise<GeneratedRequest> {
  const res = await fetch(`${API_BASE}/agents/${agentKey}/generate-request?use_rml=${useRml}`, {
    headers: authHeaders(),
  })
  return handleResponse<GeneratedRequest>(res)
}

// ─── Practitioners (public team page) ────────────────────────────────────────

export interface Practitioner {
  counselor_id: string
  client_id: string
  name: string
  title: string
  description: string
  rating: number | string   // API may return as string; normalise with Number()
  years_experience: number
  sessions_completed: number
  response_time: string
  specialties: string[]
  credentials: string[]
  availability: string
  avatar_url?: string | null
  is_active: boolean
}

export interface TenantPractitioner extends Practitioner {
  invite_email?: string | null
  invite_phone?: string | null
  source?: 'seed' | 'tenant'
}

export interface CreateTenantPractitionerRequest {
  full_name: string
  email?: string
  phone?: string
  title?: string
  description?: string
  specializations?: string[]
  credentials?: string[]
  years_experience?: number
  avatar_url?: string | null
}

interface PractitionerInviteMeta {
  invite_email?: string
  invite_phone?: string
}

interface YogaTenantContext {
  subdomain: string
  isFranchise: boolean
  clientId: string
  displayName: string
}

/** Known deployment platform patterns that are NOT real franchise subdomains */
const DEPLOYMENT_HOSTNAME_PATTERNS = [
  // Cloud Run: {service}-{hash}.{region}.run.app
  /\.run\.app$/,
  // Vercel: {project}-{hash}-{scope}.vercel.app
  /\.vercel\.app$/,
  // Netlify: {hash}--{site}.netlify.app
  /\.netlify\.app$/,
  // Render: {service}.onrender.com
  /\.onrender\.com$/,
]

function getSubdomain(): string {
  if (typeof window === 'undefined') return ''
  const hostname = window.location.hostname.toLowerCase()

  // Localhost / IP — no subdomain
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return ''

  // Deployment platform URLs are NOT franchise subdomains
  if (DEPLOYMENT_HOSTNAME_PATTERNS.some(re => re.test(hostname))) return ''

  const parts = hostname.split('.')
  // Need at least 3 parts for a real subdomain (sub.domain.tld)
  return parts.length >= 3 ? parts[0] : ''
}

function formatTenantName(subdomain: string): string {
  if (!subdomain) return 'Club of Royals'
  return subdomain
    .split(/[-_]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Derive the franchise client_id from a member's email domain.
 * e.g. shipra@allscrum.com → "allscrum" → "all-scrum-d2d807"
 * Falls back to the platform client_id if no match.
 */
export function getTenantClientIdFromEmail(email: string): string {
  const subdomain = email.split('@')[1]?.split('.')[0] ?? ''
  return TENANT_CLIENT_ID_MAP[subdomain] ?? _YOGA_CLIENT_ID
}

export function getYogaTenantContext(): YogaTenantContext {
  const subdomain = getSubdomain()
  const mappedClientId = TENANT_CLIENT_ID_MAP[subdomain]
  return {
    subdomain,
    isFranchise: subdomain !== '',
    clientId: mappedClientId ?? (subdomain ? `${_YOGA_CLIENT_ID}--${subdomain}` : _YOGA_CLIENT_ID),
    displayName: formatTenantName(subdomain),
  }
}

function practitionerKey(practitioner: Pick<Practitioner, 'name' | 'title'>): string {
  return `${practitioner.name.trim().toLowerCase()}::${practitioner.title.trim().toLowerCase()}`
}

const TENANT_PRACTITIONER_SEEDS: Record<string, TenantPractitioner[]> = {
  allscrum: [
    {
      counselor_id: 'seed-allscrum-yogi-arjun-patel',
      client_id: `${_YOGA_CLIENT_ID}--allscrum`,
      name: 'Yogi Arjun Patel',
      title: 'Senior Yoga Instructor',
      description: 'Dedicated to sharing the transformative power of yoga through personalized instruction, mindful alignment, and restorative class design.',
      rating: 4.9,
      years_experience: 12,
      sessions_completed: 124,
      response_time: '< 2 hours',
      specialties: ['Hatha Yoga', 'Vinyasa Flow', 'Therapeutic Yoga'],
      credentials: ['RYT-500', 'Yoga Therapy', 'Prenatal Yoga'],
      availability: 'Available',
      avatar_url: null,
      is_active: true,
      invite_email: 'arjun.patel@clubofroyals.net',
      invite_phone: '+1 (612) 555-0101',
      source: 'seed',
    },
    {
      counselor_id: 'seed-allscrum-priya-singh',
      client_id: `${_YOGA_CLIENT_ID}--allscrum`,
      name: 'Priya Singh',
      title: 'Meditation & Breathwork Instructor',
      description: 'Specializes in mindfulness, guided meditation, and breathwork programs that support stress relief, clarity, and deeper inner balance.',
      rating: 4.8,
      years_experience: 10,
      sessions_completed: 96,
      response_time: '< 3 hours',
      specialties: ['Mindfulness', 'Breathwork', 'Meditation'],
      credentials: ['Meditation Teacher', 'Mindfulness Coach', 'Stress Management'],
      availability: 'Available',
      avatar_url: null,
      is_active: true,
      invite_email: 'priya.singh@clubofroyals.net',
      invite_phone: '+1 (612) 555-0102',
      source: 'seed',
    },
  ],
}

function getSeedPractitioners(clientId: string): TenantPractitioner[] {
  const { subdomain } = getYogaTenantContext()
  return (TENANT_PRACTITIONER_SEEDS[subdomain] ?? []).map(practitioner => ({
    ...practitioner,
    client_id: clientId,
  }))
}

function getStoredPractitionerMeta(clientId: string): Record<string, PractitionerInviteMeta> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(`${PRACTITIONER_META_KEY}:${clientId}`)
    if (!raw) return {}
    return JSON.parse(raw) as Record<string, PractitionerInviteMeta>
  } catch {
    return {}
  }
}


function mergeTenantPractitioners(
  clientId: string,
  practitioners: Practitioner[],
): TenantPractitioner[] {
  const inviteMeta = getStoredPractitionerMeta(clientId)
  const merged = new Map<string, TenantPractitioner>()

  for (const seed of getSeedPractitioners(clientId)) {
    merged.set(practitionerKey(seed), {
      ...seed,
      invite_email: seed.invite_email ?? null,
      invite_phone: seed.invite_phone ?? null,
      source: 'seed',
    })
  }

  for (const practitioner of practitioners) {
    const key = practitionerKey(practitioner)
    const meta = inviteMeta[practitioner.counselor_id]
    merged.set(key, {
      ...(merged.get(key) ?? {}),
      ...practitioner,
      invite_email: meta?.invite_email ?? null,
      invite_phone: meta?.invite_phone ?? null,
      source: 'tenant',
    })
  }

  return Array.from(merged.values()).sort((left, right) => left.name.localeCompare(right.name))
}

/**
 * Fetch active practitioners for a client — no auth required.
 * Backed by GET /api/counselors/public/{client_id}
 */
export async function fetchPublicPractitioners(
  clientId: string,
): Promise<{ practitioners: Practitioner[]; total: number }> {
  const res = await fetchWithTimeout(`/api/counselors/public/${encodeURIComponent(clientId)}`)
  return handleResponse<{ practitioners: Practitioner[]; total: number }>(res)
}

export async function fetchTenantPublicPractitioners(): Promise<{ practitioners: TenantPractitioner[]; total: number }> {
  const tenant = getYogaTenantContext()
  try {
    const res = await fetchPublicPractitioners(tenant.clientId)
    const practitioners = mergeTenantPractitioners(tenant.clientId, res.practitioners)
    return { practitioners, total: practitioners.length }
  } catch {
    const practitioners = mergeTenantPractitioners(tenant.clientId, [])
    return { practitioners, total: practitioners.length }
  }
}

/**
 * Fetch practitioners for a partner brand within a tenant.
 * Tries {clientId}--{partnerSlug} first; inherits from {clientId} if none found.
 */
export async function fetchPartnerPublicPractitioners(
  clientId: string,
  partnerSlug: string,
): Promise<{ practitioners: TenantPractitioner[]; total: number }> {
  const partnerClientId = `${clientId}--${partnerSlug}`
  try {
    const res = await fetchPublicPractitioners(partnerClientId)
    if (res.practitioners.length > 0) {
      const practitioners = mergeTenantPractitioners(partnerClientId, res.practitioners)
      return { practitioners, total: practitioners.length }
    }
  } catch { /* fall through to inherit */ }
  // Inherit from the main tenant client
  try {
    const res = await fetchPublicPractitioners(clientId)
    const practitioners = mergeTenantPractitioners(clientId, res.practitioners)
    return { practitioners, total: practitioners.length }
  } catch {
    const practitioners = mergeTenantPractitioners(clientId, [])
    return { practitioners, total: practitioners.length }
  }
}

export async function fetchTenantPractitioners(): Promise<TenantPractitioner[]> {
  const tenant = getYogaTenantContext()
  try {
    const res = await fetch(`/api/counselors?client_id=${encodeURIComponent(tenant.clientId)}&limit=100`, {
      headers: authHeaders(),
    })
    const body = await handleResponse<{ counselors?: Practitioner[] } | Practitioner[]>(res)
    const practitioners = Array.isArray(body) ? body : body.counselors ?? []
    return mergeTenantPractitioners(tenant.clientId, practitioners)
  } catch {
    return mergeTenantPractitioners(tenant.clientId, [])
  }
}

export async function createTenantPractitioner(
  body: CreateTenantPractitionerRequest,
): Promise<{ counselor_id: string; client_id: string; action: string; email_sent: boolean }> {
  const tenant = getYogaTenantContext()
  const res = await fetch('/api/counselors/invite-practitioner', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      client_id: tenant.clientId,
      name: body.full_name,
      title: body.title || 'Yoga Practitioner',
      description: body.description || '',
      specialties: body.specializations || [],
      credentials: body.credentials || [],
      years_experience: body.years_experience || 0,
      invite_email: body.email || null,
      invite_phone: body.phone || null,
      portal_url: window.location.origin,
    }),
  })
  const created = await handleResponse<{ counselor_id: string; action: string; email_sent: boolean }>(res)
  return { counselor_id: created.counselor_id, client_id: tenant.clientId, action: created.action, email_sent: created.email_sent }
}

export async function fetchPendingPractitionerInvites(): Promise<TenantPractitioner[]> {
  const tenant = getYogaTenantContext()
  try {
    const res = await fetch(`/api/counselors/invite?client_id=${encodeURIComponent(tenant.clientId)}`, {
      headers: authHeaders(),
    })
    const list: Practitioner[] = await handleResponse<Practitioner[]>(res)
    return list.map(p => ({
      counselor_id: p.counselor_id,
      client_id: p.client_id,
      name: p.name,
      title: p.title,
      description: p.description || '',
      specialties: p.specialties || [],
      credentials: p.credentials || [],
      years_experience: p.years_experience || 0,
      rating: p.rating || 0,
      sessions_completed: p.sessions_completed || 0,
      response_time: p.response_time || '—',
      availability: p.availability || 'Pending',
      is_active: false,
      source: 'tenant' as const,
    }))
  } catch {
    return []
  }
}

// ─── Events (public) ──────────────────────────────────────────────────────────

export interface ProgramEvent {
  id: string
  client_id: string
  title: string
  type: string
  date: string
  time: string
  location: string
  format: string
  description: string
  attendees: number
  maxAttendees: number
  host: string
  is_active: boolean
  created_at: string
}

/**
 * Fetch active events for a client — no auth required.
 * Backed by GET /api/programs/events?client_id=...
 */
export async function fetchPublicEvents(
  clientId: string,
  params?: { event_type?: string; upcoming_only?: boolean; search?: string },
): Promise<{ events: ProgramEvent[]; total: number }> {
  const qs = new URLSearchParams({ client_id: clientId })
  if (params?.event_type) qs.set('event_type', params.event_type)
  if (params?.upcoming_only) qs.set('upcoming_only', 'true')
  if (params?.search) qs.set('search', params.search)
  const res = await fetchWithTimeout(`/api/programs/events?${qs}`)
  return handleResponse<{ events: ProgramEvent[]; total: number }>(res)
}

// ─── Resources (public) ───────────────────────────────────────────────────────

export interface ProgramResource {
  id: string
  client_id: string
  title: string
  resource_type: string
  type: string
  description: string
  author: string
  url: string
  tags: string[]
  views: number
  downloads: number
  rating: number
  is_downloadable: boolean
  is_active: boolean
  created_at: string
}

/**
 * Fetch active resources for a client — no auth required.
 * Backed by GET /api/programs/resources?client_id=...
 */
export async function fetchPublicResources(
  clientId: string,
  params?: { resource_type?: string; search?: string },
): Promise<{ resources: ProgramResource[]; total: number }> {
  const qs = new URLSearchParams({ client_id: clientId })
  if (params?.resource_type) qs.set('resource_type', params.resource_type)
  if (params?.search) qs.set('search', params.search)
  const res = await fetchWithTimeout(`/api/programs/resources?${qs}`)
  return handleResponse<{ resources: ProgramResource[]; total: number }>(res)
}

// ─── Programs / Trainings (public) ────────────────────────────────────────────
// Backed by the shared `mentorship_programs` table.
// For Club of Royals, mentor_type encodes program category:
//   'training' | 'workshop' | 'retreat' | 'certification' | 'course'
// Fields repurposed: experience → duration, availability → schedule,
//   specialties → included features, mentees_count → enrolled count.

export interface ProgramMentor {
  id: string
  client_id: string
  name: string
  title: string
  mentor_type: string
  bio: string
  specialties: string[]
  experience: string
  availability: string
  rating: number
  mentees_count: number
  menteesCount: number
  is_available: boolean
  is_active: boolean
  created_at: string
}

// ─── Platform (om_open_ai) registration API ──────────────────────────────────
// Used by FranchiseOnboarding to create a new client_owner account in the
// shared om_open_ai backend. Three-step flow:
//   1. register-listener → creates auth_users row
//   2. login            → issues JWT
//   3. register-client  → creates clients row + assigns client_owner role

export interface PlatformRegisterData {
  email: string
  username: string
  password: string
  full_name?: string
  phone?: string
}

export interface PlatformLoginResponse {
  access_token: string
  token_type: string
  user: Record<string, unknown>
}

export interface PlatformClientData {
  company_name: string
  industry?: string
  company_address?: string
  company_phone?: string
  website?: string
  selected_package?: string
}

export interface PlatformClientResponse {
  client_id: string
  message: string
  user: Record<string, unknown>
  access_token?: string
}

export interface TenantSessionBookingRequest {
  full_name: string
  email: string
  phone?: string
  class_name: string
  notes?: string
}

export interface TenantSessionBooking {
  booking_id: string
  client_id: string
  full_name: string
  email: string
  phone?: string
  class_name: string
  notes?: string
  created_at: string
  synced_to_client: boolean
}

export interface TenantClientUser {
  id: string
  email: string
  username: string
  full_name: string
  phone?: string
  role: string
  status: 'active' | 'pending' | 'inactive'
  created_at: string
}

export interface TenantClassPractitionerRef {
  counselor_id: string
  name: string
  title?: string
}

export interface TenantClassCustomerRef {
  id: string
  name: string
  email: string
  plan?: string
}

export interface TenantClassSampleRequest {
  class_name: string
  date: string
  time: string
  capacity: number
  assigned_practitioners: TenantClassPractitionerRef[]
  sample_customers: TenantClassCustomerRef[]
}

export interface TenantClassSampleRecord extends TenantClassSampleRequest {
  class_id: string
  client_id: string
  created_at: string
  backend_event_id?: string
  saved_to_backend: boolean
}

export interface TenantClassSyncResult {
  created: number
  skipped: number
  failed: number
}

function randomSuffix(length = 6): string {
  return Math.random().toString(36).slice(2, 2 + length)
}

function createGeneratedPassword(): string {
  return `Yoga#${randomSuffix(10)}A1`
}

function createUsernameFromEmail(email: string): string {
  const local = email.split('@')[0] || 'member'
  const safe = local.toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 18) || 'member'
  return `${safe}-${randomSuffix(4)}`
}

function getInviteTokens(): string[] {
  if (typeof window === 'undefined') return []
  const tokenSet = new Set<string>()
  const authToken = window.localStorage.getItem('auth_token')
  const yogaToken = window.localStorage.getItem('yoga_token')
  if (authToken) tokenSet.add(authToken)
  if (yogaToken) tokenSet.add(yogaToken)
  return Array.from(tokenSet)
}

function getEventCreationTokens(): Array<string | null> {
  if (typeof window === 'undefined') return [null]
  const tokenSet = new Set<string | null>()
  tokenSet.add(window.localStorage.getItem('auth_token'))
  tokenSet.add(window.localStorage.getItem('yoga_token'))
  tokenSet.add(null)
  return Array.from(tokenSet)
}

function bookingStorageKey(clientId: string): string {
  return `${TENANT_BOOKINGS_KEY}:${clientId}`
}

function classSampleStorageKey(clientId: string): string {
  return `${TENANT_CLASS_SAMPLES_KEY}:${clientId}`
}

function readBookings(clientId: string): TenantSessionBooking[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(bookingStorageKey(clientId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as TenantSessionBooking[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeBookings(clientId: string, bookings: TenantSessionBooking[]): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(bookingStorageKey(clientId), JSON.stringify(bookings))
}

function readClassSamples(clientId: string): TenantClassSampleRecord[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(classSampleStorageKey(clientId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as TenantClassSampleRecord[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeClassSamples(clientId: string, records: TenantClassSampleRecord[]): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(classSampleStorageKey(clientId), JSON.stringify(records))
}

function classSampleIdentity(record: Pick<TenantClassSampleRecord, 'class_name' | 'date' | 'time'>): string {
  return `${record.class_name.trim().toLowerCase()}|${record.date.trim()}|${record.time.trim().toLowerCase()}`
}

function upsertClassSampleRecord(clientId: string, record: TenantClassSampleRecord): void {
  const existing = readClassSamples(clientId)
  const identity = classSampleIdentity(record)
  const next = [record, ...existing.filter(item => classSampleIdentity(item) !== identity)]
  writeClassSamples(clientId, next)
}

export function getTenantClassSamples(clientId?: string): TenantClassSampleRecord[] {
  const resolvedClientId = clientId || getYogaTenantContext().clientId
  return readClassSamples(resolvedClientId)
}

export function clearTenantClassSamples(clientId?: string): void {
  const resolvedClientId = clientId || getYogaTenantContext().clientId
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(classSampleStorageKey(resolvedClientId))
  }
}

export async function saveTenantClassSample(
  payload: TenantClassSampleRequest,
): Promise<TenantClassSampleRecord> {
  const tenant = getYogaTenantContext()
  const classId = `${Date.now()}-${randomSuffix(6)}`

  let backendEventId: string | undefined
  let savedToBackend = false

  const sampleEnvelope = {
    source: 'studio-dashboard-sample',
    class_id: classId,
    class_name: payload.class_name,
    assigned_practitioners: payload.assigned_practitioners,
    sample_customers: payload.sample_customers,
  }

  try {
    for (const token of getEventCreationTokens()) {
      const response = await fetch(`/api/programs/events?client_id=${encodeURIComponent(tenant.clientId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title: payload.class_name,
          event_type: 'Yoga Class',
          event_date: payload.date,
          event_time: payload.time,
          location: `${tenant.displayName} Studio`,
          format: 'In-Person',
          description: `Studio class schedule sample\n\n${JSON.stringify(sampleEnvelope)}`,
          max_attendees: payload.capacity,
          host: payload.assigned_practitioners.map(practitioner => practitioner.name).join(', '),
        }),
      })

      if (!response.ok) {
        continue
      }

      const body = await response.json().catch(() => ({})) as { event_id?: string }
      backendEventId = body.event_id
      savedToBackend = true
      break
    }
  } catch {
    // Best-effort backend write only. Local tenant storage remains the fallback.
  }

  const record: TenantClassSampleRecord = {
    class_id: classId,
    client_id: tenant.clientId,
    class_name: payload.class_name,
    date: payload.date,
    time: payload.time,
    capacity: payload.capacity,
    assigned_practitioners: payload.assigned_practitioners,
    sample_customers: payload.sample_customers,
    created_at: new Date().toISOString(),
    backend_event_id: backendEventId,
    saved_to_backend: savedToBackend,
  }

  upsertClassSampleRecord(tenant.clientId, record)

  return record
}

export async function syncTenantClassSamplesToBackend(
  payloads: TenantClassSampleRequest[],
): Promise<TenantClassSyncResult> {
  const tenant = getYogaTenantContext()
  const result: TenantClassSyncResult = { created: 0, skipped: 0, failed: 0 }
  if (payloads.length === 0) return result

  const existingEventKeys = new Set<string>()
  try {
    const listed = await fetchPublicEvents(tenant.clientId, { upcoming_only: false })
    for (const event of listed.events) {
      existingEventKeys.add(`${event.title.trim().toLowerCase()}|${event.date || ''}|${(event.time || '').trim().toLowerCase()}`)
    }
  } catch {
    // Continue with best effort creation.
  }

  for (const payload of payloads) {
    const eventKey = `${payload.class_name.trim().toLowerCase()}|${payload.date}|${payload.time.trim().toLowerCase()}`
    if (existingEventKeys.has(eventKey)) {
      result.skipped += 1
      continue
    }

    const classId = `${Date.now()}-${randomSuffix(6)}`
    const sampleEnvelope = {
      source: 'studio-dashboard-seed-sync',
      class_id: classId,
      class_name: payload.class_name,
      assigned_practitioners: payload.assigned_practitioners,
      sample_customers: payload.sample_customers,
    }

    let backendEventId: string | undefined
    let savedToBackend = false
    try {
      for (const token of getEventCreationTokens()) {
        const response = await fetch(`/api/programs/events?client_id=${encodeURIComponent(tenant.clientId)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            title: payload.class_name,
            event_type: 'Yoga Class',
            event_date: payload.date,
            event_time: payload.time,
            location: `${tenant.displayName} Studio`,
            format: 'In-Person',
            description: `Studio class schedule sample (seed sync)\n\n${JSON.stringify(sampleEnvelope)}`,
            max_attendees: payload.capacity,
            host: payload.assigned_practitioners.map(practitioner => practitioner.name).join(', '),
          }),
        })

        if (!response.ok) {
          continue
        }

        const body = await response.json().catch(() => ({})) as { event_id?: string }
        backendEventId = body.event_id
        savedToBackend = true
        existingEventKeys.add(eventKey)
        result.created += 1
        break
      }

      if (!savedToBackend) {
        result.failed += 1
      }
    } catch {
      result.failed += 1
    }

    const record: TenantClassSampleRecord = {
      class_id: classId,
      client_id: tenant.clientId,
      class_name: payload.class_name,
      date: payload.date,
      time: payload.time,
      capacity: payload.capacity,
      assigned_practitioners: payload.assigned_practitioners,
      sample_customers: payload.sample_customers,
      created_at: new Date().toISOString(),
      backend_event_id: backendEventId,
      saved_to_backend: savedToBackend,
    }
    upsertClassSampleRecord(tenant.clientId, record)
  }

  return result
}

async function inviteViewerToClient(
  clientId: string,
  booking: TenantSessionBookingRequest,
): Promise<boolean> {
  const tokens = getInviteTokens()
  if (tokens.length === 0) return false

  for (const token of tokens) {
    const response = await fetch(`/api/users/invite?client_id=${encodeURIComponent(clientId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        email: booking.email,
        phone: booking.phone,
        role: 'viewer',
        full_name: booking.full_name,
        message: booking.notes || 'Booked a session from franchise yoga portal',
      }),
    })

    if (response.ok || response.status === 409) {
      return true
    }
  }

  return false
}

export function getTenantSessionBookings(clientId?: string): TenantSessionBooking[] {
  const resolvedClientId = clientId || getYogaTenantContext().clientId
  return readBookings(resolvedClientId)
}

export async function syncTenantBookingsToClient(clientId?: string): Promise<{ synced: number; total: number }> {
  const resolvedClientId = clientId || getYogaTenantContext().clientId
  const current = readBookings(resolvedClientId)
  if (current.length === 0) {
    return { synced: 0, total: 0 }
  }

  let synced = 0
  const next = [...current]
  for (let i = 0; i < next.length; i += 1) {
    const booking = next[i]
    if (booking.synced_to_client) continue
    const linked = await inviteViewerToClient(resolvedClientId, booking)
    if (linked) {
      synced += 1
      next[i] = { ...booking, synced_to_client: true }
    }
  }
  writeBookings(resolvedClientId, next)
  return { synced, total: next.length }
}

export async function bookTenantSession(
  booking: TenantSessionBookingRequest,
): Promise<{ booking: TenantSessionBooking; linkedToClient: boolean }> {
  const tenant = getYogaTenantContext()
  const email = booking.email.trim().toLowerCase()

  const registerResponse = await fetch('/api/auth/register-listener', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      full_name: booking.full_name.trim(),
      phone: booking.phone?.trim() || undefined,
      username: createUsernameFromEmail(email),
      password: createGeneratedPassword(),
    }),
  })

  if (!registerResponse.ok && registerResponse.status !== 409) {
    let detail = `HTTP ${registerResponse.status}`
    try {
      const body = await registerResponse.json()
      detail = body.detail ?? body.message ?? detail
    } catch {
      // ignored
    }
    throw new Error(detail)
  }

  const linkedToClient = await inviteViewerToClient(tenant.clientId, booking)
  const savedBooking: TenantSessionBooking = {
    booking_id: `${Date.now()}-${randomSuffix(6)}`,
    client_id: tenant.clientId,
    full_name: booking.full_name.trim(),
    email,
    phone: booking.phone?.trim() || '',
    class_name: booking.class_name,
    notes: booking.notes?.trim() || '',
    created_at: new Date().toISOString(),
    synced_to_client: linkedToClient,
  }

  const existing = readBookings(tenant.clientId)
  const deduped = existing.filter(item => item.email.toLowerCase() !== email)
  writeBookings(tenant.clientId, [savedBooking, ...deduped])

  return {
    booking: savedBooking,
    linkedToClient,
  }
}

export async function fetchTenantClientUsers(): Promise<TenantClientUser[]> {
  const tenant = getYogaTenantContext()
  const res = await fetch(`/api/users?client_id=${encodeURIComponent(tenant.clientId)}`, {
    headers: authHeaders(),
  })

  if (!res.ok) {
    return []
  }

  try {
    const users = await res.json()
    return Array.isArray(users) ? users as TenantClientUser[] : []
  } catch {
    return []
  }
}

/** Step 1 — Create a new user account (no client association yet). */
export async function registerPlatformUser(
  data: PlatformRegisterData,
): Promise<Record<string, unknown>> {
  const res = await fetchWithTimeout('/api/auth/register-listener', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse<Record<string, unknown>>(res)
}

/** Step 2 — Login with the just-created credentials; returns a JWT. */
export async function platformLogin(
  email: string,
  password: string,
): Promise<PlatformLoginResponse> {
  const res = await fetchWithTimeout('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  return handleResponse<PlatformLoginResponse>(res)
}

/** Step 3 — Create the studio/franchise org and assign the JWT owner as client_owner. */
export async function registerPlatformClient(
  token: string,
  data: PlatformClientData,
): Promise<PlatformClientResponse> {
  const res = await fetchWithTimeout('/api/auth/register-client', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })
  return handleResponse<PlatformClientResponse>(res)
}

/**
 * Fetch active programs/trainings for a client — no auth required.
 * Backed by GET /api/programs/mentors?client_id=...
 */
export async function fetchPublicPrograms(
  clientId: string,
  params?: { mentor_type?: string; search?: string; available_only?: boolean },
): Promise<{ mentors: ProgramMentor[]; total: number }> {
  const qs = new URLSearchParams({ client_id: clientId })
  if (params?.mentor_type) qs.set('mentor_type', params.mentor_type)
  if (params?.search) qs.set('search', params.search)
  if (params?.available_only) qs.set('available_only', 'true')
  const res = await fetchWithTimeout(`/api/programs/mentors?${qs}`)
  return handleResponse<{ mentors: ProgramMentor[]; total: number }>(res)
}

/** Full program details attached to an enrollment record. */
export interface EnrolledProgram {
  id: string
  name: string
  mentor_type: string
  title: string
  bio: string
  specialties: string[]
  experience: string
  availability: string
  rating: number
  mentees_count: number
  is_available: boolean
}

/** A single enrollment record returned by GET /api/programs/my-enrollments. */
export interface MyEnrollment {
  request_id: string
  mentor_id: string
  user_id: string
  user_name: string
  user_email: string
  user_role: string
  message: string
  goals: string
  status: string
  notes: string
  created_at: string | null
  program: EnrolledProgram | null
}

/**
 * Fetch all program enrollments for the authenticated user.
 * Works for any portal — uses the caller's client_id, no hardcoding.
 * Backed by GET /api/programs/my-enrollments?client_id=...&user_id=...
 */
export async function fetchMyEnrollments(
  clientId: string,
  userId: string | number,
): Promise<{ enrollments: MyEnrollment[]; total: number }> {
  const qs = new URLSearchParams({ client_id: clientId, user_id: String(userId) })
  const res = await fetchWithTimeout(`/api/programs/my-enrollments?${qs}`, { headers: authHeaders() })
  return handleResponse<{ enrollments: MyEnrollment[]; total: number }>(res)
}

// ─── Pricing Plans ────────────────────────────────────────────────────────────

export interface PricingPlan {
  plan_id: string
  product_key: string
  slug: string
  name: string
  tagline: string | null
  badge: string | null
  monthly_usd: number | null
  annual_usd: number | null
  monthly_local: number | null
  annual_local: number | null
  zone: string
  currency_code: string
  share_pct: number
  cap_multiplier: number
  highlight_color: string
  sort_order: number
  is_popular: boolean
  is_visible: boolean
  cta_text: string
  metadata: Record<string, unknown>
  features: string[]
  not_included: string[]
}

export type MarketZone = 'US' | 'EU' | 'IN' | 'EM'

export const ZONE_META: Record<MarketZone, { flag: string; label: string; currency: string; symbol: string; locale: string }> = {
  US: { flag: '🇺🇸', label: 'United States',  currency: 'USD', symbol: '$',  locale: 'en-US' },
  EU: { flag: '🇪🇺', label: 'Europe',          currency: 'EUR', symbol: '€',  locale: 'de-DE' },
  IN: { flag: '🇮🇳', label: 'India',           currency: 'INR', symbol: '₹',  locale: 'en-IN' },
  EM: { flag: '🌱',  label: 'Emerging Markets', currency: 'USD', symbol: '$',  locale: 'en-US' },
}

const EU_TZ_PREFIXES = ['Europe/', 'Atlantic/Azores', 'Atlantic/Canary', 'Atlantic/Madeira', 'Atlantic/Reykjavik']

export function detectMarketZone(): MarketZone {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const locale = (navigator.language || '').toLowerCase()
    if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta' || locale.startsWith('hi')) return 'IN'
    if (EU_TZ_PREFIXES.some(p => tz.startsWith(p))) return 'EU'
    if (tz.startsWith('America/') || tz.startsWith('US/') || tz === 'Pacific/Honolulu') return 'US'
    return 'EM'
  } catch {
    return 'US'
  }
}

export async function fetchPricingPlans(
  product: string,
  zone?: string,
): Promise<{ plans: PricingPlan[]; total: number }> {
  const p = new URLSearchParams({ product })
  if (zone) p.set('zone', zone)
  const res = await fetchWithTimeout(`/api/pricing-plans/plans?${p.toString()}`)
  return handleResponse<{ plans: PricingPlan[]; total: number }>(res)
}

/**
 * Subscribe the newly-registered member to a paid plan.
 * Calls POST /api/packages/client/{client_id}/subscribe using the member's JWT.
 */
export async function subscribeMember(
  clientId: string,
  packageId: string,
  accessToken: string,
  billingCycle: 'monthly' | 'annual' = 'monthly',
): Promise<{ subscription_id: string; package_id: string; status: string }> {
  const res = await fetch(`/api/packages/client/${encodeURIComponent(clientId)}/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ package_id: packageId, billing_cycle: billingCycle, auto_renew: true }),
  })
  return handleResponse<{ subscription_id: string; package_id: string; status: string }>(res)
}

const TENANT_MEMBERS_KEY = 'yoga_tenant_members'

export interface FranchiseMemberRegistration {
  full_name: string
  email: string
  phone?: string
  password: string
  plan?: string
  source: 'free_trial' | 'consultation'
  registered_at: string
}

export interface MemberRegisterResponse {
  user_id: number
  email: string
  username: string
  full_name: string | null
  client_id: string
  role: string
  access_token: string
  message: string
}

function readTenantMembers(clientId: string): FranchiseMemberRegistration[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(`${TENANT_MEMBERS_KEY}:${clientId}`)
    return raw ? (JSON.parse(raw) as FranchiseMemberRegistration[]) : []
  } catch {
    return []
  }
}

function writeTenantMembers(clientId: string, members: FranchiseMemberRegistration[]): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(`${TENANT_MEMBERS_KEY}:${clientId}`, JSON.stringify(members))
}

function appendTenantMember(clientId: string, member: FranchiseMemberRegistration): void {
  const current = readTenantMembers(clientId)
  const exists = current.some(m => m.email.toLowerCase() === member.email.toLowerCase())
  if (!exists) {
    writeTenantMembers(clientId, [...current, member])
  }
}

export function getTenantRegisteredMembers(clientId?: string): FranchiseMemberRegistration[] {
  const resolvedClientId = clientId || getYogaTenantContext().clientId
  return readTenantMembers(resolvedClientId)
}

/**
 * Book a session on the franchise portal — registers visitor as viewer, creates lead in parent client.
 * Public endpoint; no auth required.
 */
export async function bookFranchiseSession(data: {
  email: string
  username?: string
  password?: string  // If omitted, guest booking only
  full_name: string
  phone?: string
  franchise_client_id: string
  class_name: string
  preferred_date?: string
  preferred_time?: string
  notes?: string
}): Promise<{
  booking_id: string
  email: string
  franchise_client_id: string
  class_name: string
  user_created: boolean
  user_id?: number
  access_token?: string
  message: string
}> {
  const res = await fetch('/api/auth/franchise/book-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleResponse(res)
}

/**
 * Register a franchise member as a viewer under the current tenant's client_id.
 * Calls POST /api/auth/register-member — creates the backend user and links to client_users.
 * Also stores locally for Studio Dashboard Manage Members visibility.
 */
export async function registerFranchiseMember(
  data: { full_name: string; email: string; phone?: string; password: string; plan?: string; source: 'free_trial' | 'consultation' },
): Promise<MemberRegisterResponse> {
  const tenant = getYogaTenantContext()
  const email = data.email.trim().toLowerCase()
  const username = createUsernameFromEmail(email)

  const res = await fetch('/api/auth/register-member', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      username,
      password: data.password,
      full_name: data.full_name.trim() || null,
      phone: data.phone?.trim() || null,
      client_id: tenant.clientId,
    }),
  })

  const result = await handleResponse<MemberRegisterResponse>(res)

  // Store locally so Studio Dashboard Manage Members shows them without an API roundtrip
  appendTenantMember(tenant.clientId, {
    full_name: data.full_name,
    email,
    phone: data.phone,
    password: data.password,
    plan: data.plan,
    source: data.source,
    registered_at: new Date().toISOString(),
  })

  return result
}

export async function inviteTenantClientUser(data: {
  email: string
  username?: string
  password?: string
  full_name?: string
  phone?: string
  role?: string
}): Promise<TenantClientUser> {
  const tenant = getYogaTenantContext()
  const res = await fetch('/api/users', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({
      ...data,
      client_id: tenant.clientId,
    }),
  })
  return handleResponse<TenantClientUser>(res)
}

export async function updateTenantClientUser(
  userId: string,
  data: {
    role?: string
    status?: string
  },
): Promise<{ message: string }> {
  const tenant = getYogaTenantContext()
  const res = await fetch(`/api/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({
      ...data,
      client_id: tenant.clientId,
    }),
  })
  return handleResponse<{ message: string }>(res)
}

export async function deleteTenantClientUser(userId: string): Promise<{ message: string }> {
  const tenant = getYogaTenantContext()
  const res = await fetch(`/api/users/${userId}?client_id=${encodeURIComponent(tenant.clientId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return handleResponse<{ message: string }>(res)
}

// ─── Course content management (mini-LMS) ─────────────────────────────────────
// Backed by routers/courses.py — courses -> modules -> lessons, pricing/access
// "packages", and per-student "enrollments" (a shareable access_code grant).

export interface AdminCourse {
  course_id: string
  slug: string
  title: string
  subtitle: string
  description: string
  cover_image_url: string
  is_published: boolean
  created_at: string
  updated_at: string
  module_count: number
  lesson_count: number
  enrollment_count: number
}

export interface CourseLesson {
  lesson_id: string
  module_id: string
  title: string
  content_body: string
  video_url: string
  duration_min: number
  sort_order: number
  is_free_preview: boolean
}

export interface CourseModule {
  module_id: string
  course_id: string
  title: string
  description: string
  sort_order: number
  lessons: CourseLesson[]
}

export interface CoursePackage {
  package_id: string
  course_id: string
  name: string
  description: string
  price: number
  currency: string
  access_duration_days: number | null
  is_active: boolean
  sort_order: number
}

export interface AdminCourseDetail extends AdminCourse {
  modules: CourseModule[]
  packages: CoursePackage[]
}

export interface PublicCourseLesson {
  lesson_id: string
  title: string
  duration_min: number
  is_free_preview: boolean
  locked: boolean
  content_body: string | null
  video_url: string | null
}

export interface PublicCourseModule {
  module_id: string
  title: string
  description: string
  sort_order: number
  lessons: PublicCourseLesson[]
}

export interface PublicCourse {
  course_id: string
  title: string
  subtitle: string
  description: string
  cover_image_url: string
  unlocked: boolean
  student_name: string | null
  modules: PublicCourseModule[]
  packages: Array<{
    package_id: string
    name: string
    description: string
    price: number
    currency: string
    access_duration_days: number | null
  }>
}

export interface CourseEnrollment {
  enrollment_id: string
  student_name: string
  student_email: string
  access_code: string
  status: 'active' | 'revoked'
  granted_at: string
  redeemed_at: string | null
  package_name: string | null
  lessons_completed: number
  total_lessons: number
}

export async function fetchPublicCourse(
  clientId: string,
  slug: string,
  accessCode?: string,
): Promise<PublicCourse> {
  const qs = new URLSearchParams({ client_id: clientId, slug })
  if (accessCode) qs.set('access', accessCode)
  const res = await fetchWithTimeout(`/api/courses/public?${qs}`, { headers: authHeaders() })
  return handleResponse<PublicCourse>(res)
}

export interface PublicCourseSummary {
  course_id: string
  slug: string
  title: string
  subtitle: string | null
  description: string | null
  cover_image_url: string | null
  module_count: number
  lesson_count: number
  enrollment_count: number
  min_price: number
}

export async function fetchPublicCourseList(clientId: string): Promise<{ courses: PublicCourseSummary[] }> {
  const res = await fetchWithTimeout(`/api/courses/public/list?client_id=${encodeURIComponent(clientId)}`)
  return handleResponse<{ courses: PublicCourseSummary[] }>(res)
}


export async function fetchAdminCourses(clientId: string): Promise<{ courses: AdminCourse[] }> {
  const res = await fetchWithTimeout(`/api/courses?client_id=${encodeURIComponent(clientId)}`, {
    headers: authHeaders(),
  })
  return handleResponse<{ courses: AdminCourse[] }>(res)
}

export async function fetchAdminCourseDetail(courseId: string): Promise<AdminCourseDetail> {
  const res = await fetchWithTimeout(`/api/courses/${courseId}`, { headers: authHeaders() })
  return handleResponse<AdminCourseDetail>(res)
}

export async function createCourse(
  clientId: string,
  body: { slug: string; title: string; subtitle?: string; description?: string; cover_image_url?: string; is_published?: boolean },
): Promise<{ course_id: string }> {
  const res = await fetchWithTimeout(`/api/courses?client_id=${encodeURIComponent(clientId)}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ course_id: string }>(res)
}

export async function deleteCourse(courseId: string): Promise<{ deleted: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/${courseId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return handleResponse<{ deleted: boolean }>(res)
}

export async function createCourseModule(
  courseId: string,
  body: { title: string; description?: string; sort_order?: number },
): Promise<{ module_id: string }> {
  const res = await fetchWithTimeout(`/api/courses/${courseId}/modules`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ module_id: string }>(res)
}

export async function updateCourseModule(
  moduleId: string,
  body: { title?: string; description?: string; sort_order?: number },
): Promise<{ updated: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/modules/${moduleId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ updated: boolean }>(res)
}

export async function deleteCourseModule(moduleId: string): Promise<{ deleted: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/modules/${moduleId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return handleResponse<{ deleted: boolean }>(res)
}

export async function createCourseLesson(
  moduleId: string,
  body: {
    title: string
    content_body?: string
    video_url?: string
    duration_min?: number
    sort_order?: number
    is_free_preview?: boolean
  },
): Promise<{ lesson_id: string }> {
  const res = await fetchWithTimeout(`/api/courses/modules/${moduleId}/lessons`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ lesson_id: string }>(res)
}

export async function updateCourseLesson(
  lessonId: string,
  body: Partial<{
    title: string
    content_body: string
    video_url: string
    duration_min: number
    sort_order: number
    is_free_preview: boolean
  }>,
): Promise<{ updated: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/lessons/${lessonId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ updated: boolean }>(res)
}

export async function deleteCourseLesson(lessonId: string): Promise<{ deleted: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/lessons/${lessonId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return handleResponse<{ deleted: boolean }>(res)
}

export async function createCoursePackage(
  courseId: string,
  body: { name: string; description?: string; price?: number; currency?: string; access_duration_days?: number | null },
): Promise<{ package_id: string }> {
  const res = await fetchWithTimeout(`/api/courses/${courseId}/packages`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ package_id: string }>(res)
}

export async function updateCoursePackage(
  packageId: string,
  body: Partial<{ name: string; description: string; price: number; currency: string; access_duration_days: number | null; is_active: boolean }>,
): Promise<{ updated: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/packages/${packageId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ updated: boolean }>(res)
}

export async function deleteCoursePackage(packageId: string): Promise<{ deactivated: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/packages/${packageId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  return handleResponse<{ deactivated: boolean }>(res)
}

export async function fetchCourseEnrollments(courseId: string, clientId: string): Promise<{ enrollments: CourseEnrollment[] }> {
  const res = await fetchWithTimeout(`/api/courses/${courseId}/enrollments?client_id=${encodeURIComponent(clientId)}`, {
    headers: authHeaders(),
  })
  return handleResponse<{ enrollments: CourseEnrollment[] }>(res)
}

export async function grantCourseAccess(
  courseId: string,
  clientId: string,
  body: { student_name: string; student_email: string; package_id?: string },
): Promise<{ enrollment_id: string; access_code: string }> {
  const res = await fetchWithTimeout(`/api/courses/${courseId}/enrollments?client_id=${encodeURIComponent(clientId)}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  return handleResponse<{ enrollment_id: string; access_code: string }>(res)
}

export async function updateCourseEnrollmentStatus(
  enrollmentId: string,
  status: 'active' | 'revoked',
): Promise<{ updated: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/enrollments/${enrollmentId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ status }),
  })
  return handleResponse<{ updated: boolean }>(res)
}

export async function markLessonComplete(lessonId: string, accessCode: string): Promise<{ completed: boolean }> {
  const res = await fetchWithTimeout(`/api/courses/lessons/${lessonId}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_code: accessCode }),
  })
  return handleResponse<{ completed: boolean }>(res)
}

// ─── Bhagavad Gita API ───────────────────────────────────────────────────────

export interface GitaChapterMeta {
  num: number
  nameDevanagari: string
  nameIast: string
  nameEn: string
  verseCount: number
  summary: string
  videoUrl?: string | null
}

export interface GitaWordMeaning {
  word: string
  meaning: string
}

export interface GitaVerse {
  id: number
  verse: string
  devanagari: string
  iast: string
  wordMeanings: GitaWordMeaning[]
  en: string
  videoUrl?: string | null
  /** Translation in the requested language (present when lang !== 'en' and translation exists) */
  [lang: string]: unknown
}

export interface GitaChapterData {
  num: number
  nameDevanagari: string
  nameIast: string
  nameEn: string
  verseCount: number
  summary: string
  lang: string
  isPreview: boolean
  previewLimit?: number
  verses: GitaVerse[]
  videoUrl?: string | null
}

const GITA_CACHE_TTL_MS = 24 * 60 * 60 * 1000  // 24 hours

function _gitaCacheKey(chNum: number, lang: string): string {
  return `gita_ch${chNum}_${lang}_v2`
}

function _readGitaCache(chNum: number, lang: string): GitaChapterData | null {
  try {
    const raw = localStorage.getItem(_gitaCacheKey(chNum, lang))
    if (!raw) return null
    const { data, ts } = JSON.parse(raw) as { data: GitaChapterData; ts: number }
    if (Date.now() - ts > GITA_CACHE_TTL_MS) {
      localStorage.removeItem(_gitaCacheKey(chNum, lang))
      return null
    }
    return data
  } catch {
    return null
  }
}

function _writeGitaCache(chNum: number, lang: string, data: GitaChapterData): void {
  try {
    localStorage.setItem(_gitaCacheKey(chNum, lang), JSON.stringify({ data, ts: Date.now() }))
  } catch {
    // ignore storage quota errors
  }
}

/** Public — fetch chapter metadata list (no verse content). */
export async function fetchGitaChapters(): Promise<GitaChapterMeta[]> {
  const cacheKey = 'gita_chapters_meta_v2'
  try {
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      const { data, ts } = JSON.parse(cached) as { data: GitaChapterMeta[]; ts: number }
      if (Date.now() - ts < GITA_CACHE_TTL_MS) return data
    }
  } catch { /* ignore */ }

  const res = await fetchWithTimeout('/api/yoga/gita/chapters')
  const data = await handleResponse<GitaChapterMeta[]>(res)
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ data, ts: Date.now() }))
  } catch { /* ignore */ }
  return data
}

/**
 * Public — first 3 verses of a chapter (English only).
 * Used for the guest preview gate.
 */
export async function fetchGitaChapterPreview(chNum: number): Promise<GitaChapterData> {
  const cached = _readGitaCache(chNum, 'preview')
  if (cached) return cached
  const res = await fetchWithTimeout(`/api/yoga/gita/chapters/${chNum}/preview`)
  const data = await handleResponse<GitaChapterData>(res)
  _writeGitaCache(chNum, 'preview', data)
  return data
}

/**
 * Auth required — full chapter with all verses and requested translation.
 * Falls back to English if the translation is not yet generated.
 */
export async function fetchGitaChapter(chNum: number, lang = 'en'): Promise<GitaChapterData> {
  const cached = _readGitaCache(chNum, lang)
  if (cached) return cached
  const res = await fetchWithTimeout(`/api/yoga/gita/chapters/${chNum}?lang=${lang}`, {
    headers: authHeaders(),
  })
  const data = await handleResponse<GitaChapterData>(res)
  _writeGitaCache(chNum, lang, data)
  return data
}

/** Evict cached chapter data so a fresh fetch picks up new translations. */
export function invalidateGitaCache(chNum: number, lang: string): void {
  try { localStorage.removeItem(_gitaCacheKey(chNum, lang)) } catch { /* ignore */ }
}

/** Auth required — on-demand LLM translation for a chapter. Returns count generated. */
export async function triggerGitaTranslation(chNum: number, lang: string): Promise<{ generated: number }> {
  const res = await fetchWithTimeout(`/api/yoga/gita/chapters/${chNum}/translate?lang=${lang}`, {
    method: 'POST',
    headers: authHeaders(),
  })
  return handleResponse(res)
}

/** Auth required — translation coverage per language. */
export async function fetchGitaTranslationStatus(): Promise<{
  total_verses: number
  languages: Record<string, { count: number; complete: boolean }>
}> {
  const res = await fetchWithTimeout('/api/yoga/gita/translation-status', {
    headers: authHeaders(),
  })
  return handleResponse(res)
}

